import { Link } from "react-router-dom";
import { Heart, ArrowRight, Lock, ExternalLink } from "lucide-react";

const CONVERGE_PAY_URL =
  "https://www.convergepay.com/hosted-payments?ssl_txn_auth_token=ZKbWdA9USIiLGtDEaQX9agAAAZ22PE1I";

const SUGGESTED_AMOUNTS = [
  { amount: 25, label: "App access", desc: "Sponsors one person's app access for a month." },
  { amount: 50, label: "Insurance navigation", desc: "pVerify coverage + first provider connection." },
  { amount: 100, label: "A month of care", desc: "Funds a month of peer & AI companion support." },
  { amount: 250, label: "A lifeline", desc: "Trains and certifies one peer supporter." },
  { amount: 500, label: "A movement", desc: "Funds outreach + app sponsorships across a community." },
];

export default function Donate() {
  return (
    <div data-testid="donate-page" className="bg-white">
      {/* HERO */}
      <section className="py-20 lg:py-28 bg-gradient-hero relative overflow-hidden" data-testid="donate-hero">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-teal/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-5">Make it possible</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-navy leading-[1.05]">
            Your gift puts this app in the hands of <span className="hl-teal">someone who can't afford care.</span>
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-muted-ink leading-relaxed max-w-2xl mx-auto">
            100% of donations fund access — sponsoring the app for underserved individuals, funding peer certification,
            and keeping our crisis line open. Every dollar breaks a cycle.
          </p>
        </div>
      </section>

      {/* PRIMARY DONATE CARD */}
      <section className="pb-16" data-testid="donate-action-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="gh-card !p-10 lg:!p-14 text-center">
            <div className="w-16 h-16 rounded-2xl bg-teal-light text-teal flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8" fill="currentColor" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-navy mb-4 leading-[1.1]">
              Donate securely in <span className="hl-teal">under 60 seconds.</span>
            </h2>
            <p className="text-muted-ink leading-relaxed mb-8 max-w-xl mx-auto">
              You'll be redirected to our secure payment page where you can choose your amount and complete
              your gift. Card and electronic payments accepted.
            </p>
            <a
              href={CONVERGE_PAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-lg !px-10"
              data-testid="donate-converge-button"
            >
              Donate now <ExternalLink className="w-5 h-5" />
            </a>
            <p className="mt-6 text-xs text-muted-ink flex items-center justify-center gap-1.5">
              <Lock className="w-3.5 h-3.5" /> Payments processed securely. Generational Health is a mission-driven organization.
            </p>
          </div>
        </div>
      </section>

      {/* SUGGESTED AMOUNTS */}
      <section className="py-16 bg-cream" data-testid="donate-suggested-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <p className="eyebrow mb-3">Suggested gifts</p>
            <h2 className="font-display text-3xl sm:text-4xl text-navy leading-[1.1]">
              See what your gift can <span className="hl-teal">make happen.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {SUGGESTED_AMOUNTS.map((s) => (
              <div
                key={s.amount}
                className="gh-card !p-5 text-center"
                data-testid={`suggested-${s.amount}`}
              >
                <p className="font-display text-3xl text-navy mb-2">${s.amount}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-teal mb-2">{s.label}</p>
                <p className="text-xs text-muted-ink leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href={CONVERGE_PAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-teal"
              data-testid="suggested-give-button"
            >
              Give any amount <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* IMPACT BREAKDOWN */}
      <section className="py-20" data-testid="donate-impact-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <p className="eyebrow mb-3 text-center">Where your gift goes</p>
          <h2 className="font-display text-4xl sm:text-5xl text-navy text-center leading-[1.05] mb-12">
            Every dollar = <span className="hl-teal">access restored.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: "68¢", l: "Direct care", d: "Sponsored app access, provider hours, crisis response." },
              { n: "24¢", l: "Training & access", d: "Peer certification, sliding-scale subsidies, outreach." },
              { n: "8¢", l: "Infrastructure", d: "HIPAA security, technology, small operations team." },
            ].map((x, i) => (
              <div key={i} className="gh-card text-center" data-testid={`impact-${i}`}>
                <p className="font-display text-6xl text-teal mb-2">{x.n}</p>
                <p className="eyebrow mb-3">{x.l}</p>
                <p className="text-muted-ink text-[15px]">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER PROMPT */}
      <section className="py-20 bg-navy text-white relative overflow-hidden" data-testid="donate-partner-section">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-teal/15 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-white mb-5 leading-[1.1]">
            Looking to fund at <span className="hl-teal">organizational scale?</span>
          </h2>
          <p className="text-white/80 mb-8">
            Schools, employers, and grant-makers — let's talk about a structured partnership.
          </p>
          <Link to="/partner" className="btn-outline-teal !border-white !text-white hover:!bg-white hover:!text-navy" data-testid="donate-partner-cta">
            Partner with us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
